import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { trackPricingView } from '@/lib/analytics'
import { Check, X, ArrowRight } from 'lucide-react'

const packages = [
  {
    title: 'Self-Serve',
    priceRange: '$20–50/mo',
    badge: null,
    whoFor: 'For DIY orgs who want a modern site and manage their own content',
    transformation: 'Own your site without wrestling with Squarespace or WordPress. Same CMS we use for paid clients.',
    socialProof: 'Base platform, no setup fees',
    description: 'The Fugue Lab CMS on a custom domain. You manage your own content; we keep the platform running.',
    features: [
      'Hosted CMS with custom domain',
      'Mobile-optimized templates',
      'SSL + global CDN',
      'Email support',
      'Upgrade anytime as you grow',
    ],
    timeline: 'Launch in 1–2 weeks',
  },
  {
    title: 'Launch',
    priceRange: '$50–150/mo',
    badge: 'MOST POPULAR',
    whoFor: 'For orgs that need a new site built once, then light ongoing help',
    transformation: 'Fugue Lab builds your site; you own the content afterward. Setup cost amortized over year one.',
    socialProof: 'Most new clients start here',
    description:
      'Base platform + one-time build spread over 12 months. Content-ready setup with training so you can take the wheel.',
    features: [
      'Everything in Self-Serve',
      'Custom design & build',
      'Content migration from your current site',
      'SEO setup + Google Analytics',
      'Training session + handoff docs',
      'Light updates during year one',
    ],
    timeline: '2–6 weeks to launch',
  },
  {
    title: 'Growth',
    priceRange: '$150–300/mo',
    badge: null,
    whoFor: 'For orgs that want a partner on updates, email, and seasonal campaigns',
    transformation: 'A working musician and engineer handles the digital work so you can focus on the music',
    socialProof: 'Predictable monthly vs. ad-hoc chaos',
    description:
      'Everything in Launch, plus ongoing management: content updates, email campaigns, and seasonal strategy.',
    features: [
      'Everything in Launch',
      'Monthly website updates',
      'Email campaign management',
      'Seasonal content planning',
      'Quarterly strategy calls',
      'Priority email support',
    ],
    timeline: 'Ongoing',
  },
  {
    title: 'Full-Service',
    priceRange: '$300–600+/mo',
    badge: 'FESTIVALS',
    whoFor: 'For festivals, orchestras, and multi-program orgs that need everything managed',
    transformation: 'End-to-end digital partner: site, email, applications, ticketing, and strategy',
    socialProof: 'Built for multi-program organizations',
    description:
      'Everything in Growth, plus custom integrations: applications, ticketing, donor flows, and advanced reporting.',
    features: [
      'Everything in Growth',
      'Application & ticketing systems',
      'Donor & fundraising flows',
      'Custom integrations (Stripe, CRM, etc.)',
      'Monthly strategy calls',
      'On-call support during key seasons',
    ],
    timeline: 'Ongoing',
  },
]

export default function PackagesPage() {
  useEffect(() => {
    trackPricingView('/packages')
  }, [])
  const breadcrumbs = generateBreadcrumbs('/packages', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useScrollReveal<HTMLDivElement>('.reveal-item', { stagger: 0.08 })
  const packagesRef = useStaggeredGrid<HTMLElement>('.package-card', { stagger: 0.1 })
  const howItWorksRef = useScrollReveal<HTMLDivElement>('.step-item', { stagger: 0.12 })

  useSEO({
    title: 'Packages & Pricing',
    description:
      'Transparent monthly pricing for classical music organizations. Self-serve CMS, done-for-you website builds, ongoing digital management, and full-service festival support, all on one platform.',
    url: '/packages',
    canonical: '/packages',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'OfferCatalog',
      name: 'Fugue Lab Service Packages',
      description: 'Digital strategy packages for classical music organizations',
      provider: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="mx-auto space-y-16">
      {/* Hero */}
      <section className="relative min-h-[90vh] w-full overflow-hidden md:h-[75vh]">
        <img
          src="/servers.jpg"
          alt="Digital strategy for classical music festivals"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div
          ref={heroRef}
          className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center gap-y-6 px-8 text-center text-white"
        >
          <h1 className="text-4xl font-bold tracking-tight">Monthly Pricing for Classical Music Organizations</h1>
          <p className="max-w-2xl text-lg leading-relaxed text-white/90">
            One platform, one monthly price. Built for festivals, orchestras, opera companies, choral groups, and church
            music programs, from DIY to fully managed.
          </p>
          <div className="mt-2 flex flex-col gap-2 text-sm text-white/70">
            <div className="flex items-center justify-center gap-2">
              <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>Built by a working musician and software engineer</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>Setup costs amortized over year one</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>Free strategy call to find your fit</span>
            </div>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="mt-4 cursor-pointer">
                Book a Free Call →
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem & Stakes Section */}
      <section className="bg-gray-900/50 px-8 py-16">
        <div ref={problemsRef} className="mx-auto grid max-w-5xl gap-12 md:grid-cols-2">
          <div className="reveal-item space-y-4">
            <h2 className="text-2xl font-bold text-white">Common Digital Problems Music Orgs Face</h2>
            <ul className="space-y-3 text-white/80">
              <li className="flex items-start gap-2">
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" strokeWidth={2.5} />
                <span>Outdated website that doesn&apos;t reflect program quality</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" strokeWidth={2.5} />
                <span>Confusing registration or application process losing qualified people</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" strokeWidth={2.5} />
                <span>Manual handling of applications, payments, and communications</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" strokeWidth={2.5} />
                <span>No visibility into who&apos;s engaging or what&apos;s working</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" strokeWidth={2.5} />
                <span>Staff overwhelmed with administrative chaos</span>
              </li>
            </ul>
          </div>
          <div className="reveal-item space-y-4">
            <h2 className="text-2xl font-bold text-white">What This Costs Your Organization</h2>
            <ul className="space-y-3 text-white/80">
              <li className="flex items-start gap-2">
                <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                <span>Lower enrollment and attendance from poor digital experience</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                <span>Dozens of hours wasted on manual processes</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                <span>Lost audience to organizations with better sites</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                <span>Missed donor and sponsorship opportunities</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                <span>Constant stress about your digital presence</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 text-center">
          <p className="text-lg font-medium text-white/90">
            There&apos;s a better way. Choose a package and let us handle the digital strategy.
          </p>
        </div>
      </section>

      {/* Section Intro */}
      <section className="px-8 text-center">
        <div className="space-y-4 text-center">
          <h2 className="text-2xl font-bold">Four Ways to Work Together</h2>
          <p className="text-muted-foreground mx-auto max-w-2xl">
            Start wherever you are. Every tier runs on the same Fugue Lab platform, so you can move up as your needs
            grow without migrating your site.
          </p>
        </div>
      </section>

      {/* Packages */}
      <section ref={packagesRef} className="mx-auto grid max-w-7xl gap-8 px-8 md:grid-cols-2 lg:grid-cols-4">
        {packages.map((pkg) => (
          <motion.div
            key={pkg.title}
            className="package-card border-primary relative flex flex-col justify-between rounded-lg border p-6 shadow-sm transition hover:shadow-md"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            {pkg.badge && (
              <div className="bg-primary text-primary-foreground absolute -top-3 left-1/2 -translate-x-1/2 transform rounded-full px-3 py-1 text-xs font-bold">
                {pkg.badge}
              </div>
            )}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">{pkg.title}</h2>
              <div className="text-primary text-xl font-bold">{pkg.priceRange}</div>

              {pkg.timeline && (
                <div className="text-muted-foreground text-sm">
                  <span className="font-semibold">Timeline:</span> {pkg.timeline}
                </div>
              )}

              {/* Who For */}
              <p className="text-sm font-medium text-white/90 italic">{pkg.whoFor}</p>

              {/* Transformation */}
              <div className="bg-primary/10 border-primary/20 rounded-md border p-3">
                <p className="text-sm font-semibold text-white">{pkg.transformation}</p>
              </div>

              {/* Social Proof */}
              <div className="flex items-start gap-2 text-sm text-white/70">
                <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                <span>{pkg.socialProof}</span>
              </div>

              <p className="text-muted-foreground">{pkg.description}</p>
              <ul className="mt-4 list-inside list-disc space-y-1 text-sm">
                {pkg.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="mt-6">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Link to="/get-started">
                  <Button variant="default" className="w-full cursor-pointer">
                    Get Started
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Payment & Process Info */}
      <section className="bg-gray-900/50 px-8 py-12">
        <div ref={howItWorksRef} className="mx-auto max-w-3xl space-y-6 text-center">
          <h2 className="text-2xl font-bold text-white">How It Works</h2>
          <div className="grid gap-6 text-left md:grid-cols-3">
            <div className="step-item space-y-2">
              <div className="text-primary text-3xl font-bold">1</div>
              <h3 className="font-semibold text-white">Strategy Call</h3>
              <p className="text-sm text-white/80">
                Free 15-minute call to understand your needs and recommend the right tier.
              </p>
            </div>
            <div className="step-item space-y-2">
              <div className="text-primary text-3xl font-bold">2</div>
              <h3 className="font-semibold text-white">Proposal & Kickoff</h3>
              <p className="text-sm text-white/80">
                Receive a tailored scope and monthly price. First month starts the work.
              </p>
            </div>
            <div className="step-item space-y-2">
              <div className="text-primary text-3xl font-bold">3</div>
              <h3 className="font-semibold text-white">Build & Launch</h3>
              <p className="text-sm text-white/80">
                We handle everything and keep you updated. Training and launch support included.
              </p>
            </div>
          </div>
          <div className="pt-6">
            <p className="text-sm text-white/70">Setup costs amortized over year one. Cancel anytime after that.</p>
          </div>
        </div>
      </section>

      {/* Contact Form CTA */}
      <FugueContactForm />
    </div>
  )
}
